import {
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  GridCol,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import {
  IconCalendar,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPhoneCall,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { OrderResponse } from "types/order.types";

interface OrderDetailsProps {
  order: OrderResponse;
  getStatusColor?: (status: string) => string;
  formatCurrency?: (amount: number) => string;
}

export function OrderDetails({
  order,
  getStatusColor,
  formatCurrency,
}: OrderDetailsProps) {
  return (
    <Stack>
      <Card>
        <Grid justify="space-between" align="start">
          <Stack gap={0}>
            <Text size="sm" c="dimmed">
              {order.type}
            </Text>
            <Text fw={500}>{order.additionalData?.orderNumber ?? ""}</Text>
          </Stack>
          <Group>
            <Chip
              color={getStatusColor ? getStatusColor(order.status) : undefined}
            >
              {order.status.replace("_", " ").toUpperCase()}
            </Chip>
          </Group>
        </Grid>
        {order.customer && (
          <>
            <Divider my="sm" m="lg" />
            <Grid justify="space-between" align="start">
              <Stack gap={0}>
                <Text size="sm" c="dimmed">
                  CUSTOMER
                </Text>
                <Text>{order.customer?.displayName}</Text>
                {order.customer?.defaultPlace?.address && (
                  <Text size="sm" c="dimmed">
                    <IconMapPin size={16} style={{ marginRight: 8 }} />
                    {order.customer?.defaultPlace?.address}
                  </Text>
                )}
                <Group>
                  {order.customer?.contactPhoneNumber && (
                    <Text c="dimmed">
                      <IconPhone size={16} style={{ marginRight: 8 }} />
                      {order.customer?.contactPhoneNumber}
                    </Text>
                  )}
                  {order.customer?.email && (
                    <Text c="dimmed">
                      <IconMail size={16} style={{ marginRight: 8 }} />
                      {order.customer?.email}
                    </Text>
                  )}
                </Group>
              </Stack>
            </Grid>
          </>
        )}
      </Card>

      <Stack>
        <Text fw={500}>Activities:</Text>
        {order.activityDeliveries?.map((activity) => (
          <Card>
            <Group gap={0} justify="space-between" align="center">
              <Group gap="xs" align="center">
                <IconCalendar size={24} color="gray" />
                <Text>
                  {dayjs(activity.shippingDate).format("MMM D, YYYY")}
                </Text>
              </Group>
              <Chip>DELIVERY</Chip>
            </Group>
            <Text>Pickup: {activity.warehouse?.name}</Text>
            <Text size="sm" c="dimmed" ml="md">
              {activity.pickupPlace?.address}
            </Text>
            <Text>Dropoff: {activity.customer?.displayName}</Text>
            <Text size="sm" c="dimmed" ml="md">
              {activity.dropoffPlace?.address}
            </Text>
            <Text>Ship To Addressee: {activity.shipToAddressee}</Text>
          </Card>
        ))}
        {order.activityTransfers?.map((activity) => (
          <Card>
            <Group gap={0} justify="space-between" align="center">
              <Group gap="xs" align="center">
                <IconCalendar size={24} color="gray" />
                <Text>
                  {dayjs(activity.shippingDate).format("MMM D, YYYY")}
                </Text>
              </Group>
              <Chip>TRANSFER</Chip>
            </Group>
            <Text>Pickup: {activity.sourceWarehouse?.name}</Text>
            <Text size="sm" c="dimmed" ml="md">
              {activity.pickupPlace?.address}
            </Text>
            <Text>Dropoff: {activity.destWarehouse?.name}</Text>
            <Text size="sm" c="dimmed" ml="md">
              {activity.dropoffPlace?.address}
            </Text>
          </Card>
        ))}
      </Stack>

      <Text fw={500}>Items:</Text>
      <Card>
        <ScrollArea>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Rate</Table.Th>
                <Table.Th>Quantity</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {order.orderItems.map((item) => (
                <Table.Tr key={item.orderItemId}>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>{item.description}</Table.Td>
                  <Table.Td>
                    {formatCurrency
                      ? formatCurrency(item.rate ?? 0)
                      : item.rate}
                  </Table.Td>
                  <Table.Td>{item.quantity}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
}
